import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, FileText, Check, X, Pencil, Trash2, Clock } from 'lucide-react';
import { cn } from '../../utils/cn';

const SOURCE_META = {
  ticket_qa: { icon: MessageSquare, color: 'bg-blue-100 text-blue-700' },
  document: { icon: FileText, color: 'bg-purple-100 text-purple-700' },
};

export default function KnowledgeChunkCard({ chunk, onToggleApproval, onSave, onDelete }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ question: chunk.question, answer: chunk.answer || '' });

  const { icon: SourceIcon, color } = SOURCE_META[chunk.source_type] || SOURCE_META.document;

  const handleSave = async () => {
    await onSave(chunk.id, draft);
    setEditing(false);
  };

  return (
    <div
      className={cn(
        'bg-white rounded-xl border shadow-sm p-5',
        chunk.is_approved ? 'border-gray-200' : 'border-red-200 bg-red-50/30'
      )}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', color)}>
            <SourceIcon className="h-3 w-3" />
            {t(`knowledge.source_${chunk.source_type}`)}
          </span>
          {chunk.intent && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {t(`intent.${chunk.intent}`, { defaultValue: chunk.intent })}
            </span>
          )}
          {chunk.language && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 uppercase">
              {chunk.language}
            </span>
          )}
          {chunk.shop && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {chunk.shop.name}
            </span>
          )}
          {!chunk.embedded_at && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              <Clock className="h-3 w-3" /> {t('knowledge.awaiting_embedding')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onToggleApproval(chunk)}
            title={chunk.is_approved ? t('knowledge.reject') : t('knowledge.approve')}
            className={cn(
              'p-1.5 transition-colors',
              chunk.is_approved ? 'text-green-600 hover:text-red-600' : 'text-gray-400 hover:text-green-600'
            )}
          >
            {chunk.is_approved ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setEditing((v) => !v)}
            className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(chunk.id)}
            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <textarea
            value={draft.question}
            onChange={(e) => setDraft({ ...draft, question: e.target.value })}
            rows={3}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          />
          <textarea
            value={draft.answer}
            onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
            rows={6}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          />
          <p className="text-xs text-gray-500">{t('knowledge.edit_hint')}</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-900 whitespace-pre-line line-clamp-3">{chunk.question}</p>
          {chunk.answer && (
            <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-4">{chunk.answer}</p>
          )}
        </div>
      )}
    </div>
  );
}
